import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, Upload } from 'lucide-react';
import { saveOnboarding, submitPublicShare } from '../utils/api';
import ImageCropper from '../components/ImageCropper';
import ConsentNotice from '../components/ConsentNotice';
import { buildConsent, CONSENT_PURPOSE } from '../utils/dpdp';

const CreateOnboarding = ({ publicToken = null, onSubmitted = null }) => {
  const navigate = useNavigate();
  const [consentGiven, setConsentGiven] = useState(false);
  const [formData, setFormData] = useState({
    investor_type: 'regular', // regular, minor, nri
    name: '',
    pan: '',
    dob: '',
    father_name: '',
    mother_name: '',
    email: '',
    mobile: '',
    address: '',
    // NRI specific
    overseas_address: '',
    passport_number: '',
    passport_copy: null,
    pis_letter: null,
    // Minor specific
    guardian_name: '',
    guardian_pan: '',
    guardian_relationship: '',
    guardian_address_proof: null,
    birth_certificate: null,
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    micr_no: '',
    nominee_name: '',
    nominee_relationship: '',
    nominee_id_type: 'pan',
    nominee_id_number: '',
    nominee_mobile: '',
    nominee_email: '',
    nominee_address: '',
    nominee_dob: '',
    photo: null,
    pan_proof: null,
    bank_proof: null
  });

  const [cropData, setCropData] = useState({
    image: null,
    fieldName: null,
    aspect: 1
  });

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }

      // Only crop images, not PDFs
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          // Determine aspect ratio based on field
          const aspect = fieldName === 'photo' ? 1 : 4 / 3; // Square for photo, 4:3 for documents
          setCropData({
            image: event.target.result,
            fieldName: fieldName,
            aspect: aspect
          });
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs, just convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({ ...formData, [fieldName]: reader.result });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCropComplete = (croppedImage) => {
    setFormData({ ...formData, [cropData.fieldName]: croppedImage });
    setCropData({ image: null, fieldName: null, aspect: 1 });
  };

  const handleCropCancel = () => {
    setCropData({ image: null, fieldName: null, aspect: 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.photo) {
      alert('Please capture or upload a photo');
      return;
    }
    if (!formData.pan_proof) {
      alert('Please upload PAN proof');
      return;
    }
    if (!formData.bank_proof) {
      alert('Please upload Bank proof');
      return;
    }
    if (!consentGiven) {
      alert('Please provide consent under the Data Protection Notice before submitting.');
      return;
    }

    try {
      const onboardingData = {
        ...formData,
        consent: buildConsent(CONSENT_PURPOSE.onboarding, { assisted: !publicToken }),
        created_at: new Date().toISOString()
      };

      if (publicToken) {
        const res = await submitPublicShare(publicToken, onboardingData);
        if (onSubmitted) onSubmitted(res.data);
        return;
      }

      const recordId = await saveOnboarding(onboardingData);
      
      if (recordId) {
        navigate(`/admin/onboarding/view/${recordId}`);
      } else {
        alert('Error saving onboarding data. Please try again.');
      }
    } catch (error) {
      console.error('Error saving onboarding:', error);
      if (error.name === 'QuotaExceededError') {
        alert('Storage quota exceeded. Please try uploading smaller images (compress them first).');
      } else {
        alert('Error saving onboarding data: ' + error.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      {cropData.image && (
        <ImageCropper
          image={cropData.image}
          aspect={cropData.aspect}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
      
      <div className="max-w-5xl mx-auto">
        {!publicToken && (
          <Button
            variant="outline"
            onClick={() => navigate('/admin/onboarding')}
            className="mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Onboarding List
          </Button>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">New Investor Onboarding</CardTitle>
            <p className="text-sm text-slate-600">Complete KYC details and upload required documents</p>

              {/* Investor Type Selection */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-indigo-900">Investor Type *</h3>
                <select
                  value={formData.investor_type}
                  onChange={(e) => setFormData({ ...formData, investor_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="regular">Regular Individual</option>
                  <option value="minor">Minor (Below 18 years)</option>
                  <option value="nri">NRI (Non-Resident Indian)</option>
                </select>
              </div>

          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-blue-900">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="pan">PAN *</Label>
                    <input
                      id="pan"
                      type="text"
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                      required
                      maxLength="10"
                      pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                      placeholder="ABCDE1234F"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dob">Date of Birth *</Label>
                    <input
                      id="dob"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="father_name">Father's Name *</Label>
                    <input
                      id="father_name"
                      type="text"
                      value={formData.father_name}
                      onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mother_name">Mother's Name *</Label>
                    <input
                      id="mother_name"
                      type="text"
                      value={formData.mother_name}
                      onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email ID *</Label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <input
                      id="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      pattern="[0-9]{10}"
                      maxLength="10"
                      placeholder="10-digit mobile number"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-green-900">Bank Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bank_name">Bank Name *</Label>
                    <input
                      id="bank_name"
                      type="text"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="account_no">Account Number *</Label>
                    <input
                      id="account_no"
                      type="text"
                      value={formData.account_no}
                      onChange={(e) => setFormData({ ...formData, account_no: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="ifsc_code">IFSC Code *</Label>
                    <input
                      id="ifsc_code"
                      type="text"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                      required
                      maxLength="11"
                      placeholder="ABCD0123456"
                    />
                  </div>

                  <div>
                    <Label htmlFor="micr_no">MICR Number *</Label>
                    <input
                      id="micr_no"
                      type="text"
                      value={formData.micr_no}
                      onChange={(e) => setFormData({ ...formData, micr_no: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                      pattern="[0-9]{9}"
                      maxLength="9"
                      placeholder="9-digit MICR code"
                    />
                  </div>
                </div>
              </div>

              {/* Nominee Details */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-orange-900">Nominee Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nominee_name">Nominee Name *</Label>
                    <input
                      id="nominee_name"
                      type="text"
                      value={formData.nominee_name}
                      onChange={(e) => setFormData({ ...formData, nominee_name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nominee_relationship">Relationship with Nominee *</Label>
                    <input
                      id="nominee_relationship"
                      type="text"
                      value={formData.nominee_relationship}
                      onChange={(e) => setFormData({ ...formData, nominee_relationship: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* NRI Specific Fields */}
              {formData.investor_type === 'nri' && (
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg mb-4 text-cyan-900">NRI Specific Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="overseas_address">Overseas Address *</Label>
                      <textarea
                        id="overseas_address"
                        value={formData.overseas_address}
                        onChange={(e) => setFormData({ ...formData, overseas_address: e.target.value })}
                        className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        required={formData.investor_type === 'nri'}
                        rows="3"
                      />
                    </div>
                    <div>
                      <Label htmlFor="passport_number">Passport Number *</Label>
                      <input
                        id="passport_number"
                        type="text"
                        value={formData.passport_number}
                        onChange={(e) => setFormData({ ...formData, passport_number: e.target.value.toUpperCase() })}
                        className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 uppercase"
                        required={formData.investor_type === 'nri'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="passport_copy">Passport Copy *</Label>
                      <input
                        id="passport_copy"
                        type="file"
                        accept="image/*,.pdf"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'passport_copy')}
                        className="w-full mt-1"
                      />
                      {formData.passport_copy && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                    <div>
                      <Label htmlFor="pis_letter">PIS Permission Letter (Optional)</Label>
                      <input
                        id="pis_letter"
                        type="file"
                        accept="image/*,.pdf"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'pis_letter')}
                        className="w-full mt-1"
                      />
                      {formData.pis_letter && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Minor Specific Fields */}
              {formData.investor_type === 'minor' && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg mb-4 text-yellow-900">Guardian Information (For Minor)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="guardian_name">Guardian Name *</Label>
                      <input
                        id="guardian_name"
                        type="text"
                        value={formData.guardian_name}
                        onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                        className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        required={formData.investor_type === 'minor'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="guardian_pan">Guardian PAN *</Label>
                      <input
                        id="guardian_pan"
                        type="text"
                        value={formData.guardian_pan}
                        onChange={(e) => setFormData({ ...formData, guardian_pan: e.target.value.toUpperCase() })}
                        className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 uppercase"
                        required={formData.investor_type === 'minor'}
                        maxLength="10"
                        pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                      />
                    </div>
                    <div>
                      <Label htmlFor="guardian_relationship">Relationship *</Label>
                      <select
                        id="guardian_relationship"
                        value={formData.guardian_relationship}
                        onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })}
                        className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        required={formData.investor_type === 'minor'}
                      >
                        <option value="">Select Relationship</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="guardian">Legal Guardian</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="birth_certificate">Minor's Birth Certificate *</Label>
                      <input
                        id="birth_certificate"
                        type="file"
                        accept="image/*,.pdf"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'birth_certificate')}
                        className="w-full mt-1"
                      />
                      {formData.birth_certificate && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                    <div>
                      <Label htmlFor="guardian_address_proof">Guardian Address Proof (Aadhar/Voter ID/DL) *</Label>
                      <input
                        id="guardian_address_proof"
                        type="file"
                        accept="image/*,.pdf"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'guardian_address_proof')}
                        className="w-full mt-1"
                      />
                      {formData.guardian_address_proof && <span className="text-xs text-green-600">✓ Uploaded</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Upload */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-purple-900">Investor Photo *</h3>
                
                {!formData.photo ? (
                  <Label htmlFor="photo_upload" className="cursor-pointer">
                    <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 hover:border-purple-500 hover:bg-purple-100 text-center">
                      <Upload className="mx-auto mb-2 text-purple-400" size={48} />
                      <p className="text-sm text-purple-600 font-medium">Click to upload or capture photo</p>
                      <p className="text-xs text-purple-500 mt-1">JPG, PNG (Max 5MB)</p>
                    </div>
                    <input
                      id="photo_upload"
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={(e) => handleFileUpload(e, 'photo')}
                      className="hidden"
                    />
                  </Label>
                ) : (
                  <div className="space-y-2">
                    <img
                      src={formData.photo}
                      alt="Investor"
                      className="w-48 h-48 object-cover border-4 border-purple-300 rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, photo: null })}
                    >
                      Change Photo
                    </Button>
                  </div>
                )}
              </div>

              {/* Document Uploads */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-amber-900">Document Uploads</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* PAN Proof */}
                  <div>
                    <Label htmlFor="pan_proof">PAN Proof *</Label>
                    {!formData.pan_proof ? (
                      <Label htmlFor="pan_proof_upload" className="cursor-pointer mt-2 block">
                        <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 hover:border-amber-500 hover:bg-amber-100 text-center">
                          <Upload className="mx-auto mb-2 text-amber-400" size={32} />
                          <p className="text-sm text-amber-600">Click to upload or capture</p>
                          <p className="text-xs text-amber-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
                        </div>
                        <input
                          id="pan_proof_upload"
                          type="file"
                          accept="image/*,.pdf"
                          capture="environment"
                          onChange={(e) => handleFileUpload(e, 'pan_proof')}
                          className="hidden"
                        />
                      </Label>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {formData.pan_proof.startsWith('data:image') && (
                          <img
                            src={formData.pan_proof}
                            alt="PAN Proof"
                            className="w-full max-w-sm rounded-lg border-2 border-amber-300"
                          />
                        )}
                        <div className="p-2 bg-green-100 text-green-700 rounded text-sm flex items-center justify-between">
                          <span>✓ PAN proof uploaded</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({ ...formData, pan_proof: null })}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bank Proof */}
                  <div>
                    <Label htmlFor="bank_proof">Bank Proof (Cancelled Cheque/Statement) *</Label>
                    {!formData.bank_proof ? (
                      <Label htmlFor="bank_proof_upload" className="cursor-pointer mt-2 block">
                        <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 hover:border-amber-500 hover:bg-amber-100 text-center">
                          <Upload className="mx-auto mb-2 text-amber-400" size={32} />
                          <p className="text-sm text-amber-600">Click to upload or capture</p>
                          <p className="text-xs text-amber-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
                        </div>
                        <input
                          id="bank_proof_upload"
                          type="file"
                          accept="image/*,.pdf"
                          capture="environment"
                          onChange={(e) => handleFileUpload(e, 'bank_proof')}
                          className="hidden"
                        />
                      </Label>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {formData.bank_proof.startsWith('data:image') && (
                          <img
                            src={formData.bank_proof}
                            alt="Bank Proof"
                            className="w-full max-w-sm rounded-lg border-2 border-amber-300"
                          />
                        )}
                        <div className="p-2 bg-green-100 text-green-700 rounded text-sm flex items-center justify-between">
                          <span>✓ Bank proof uploaded</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({ ...formData, bank_proof: null })}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <ConsentNotice
                purpose={CONSENT_PURPOSE.onboarding}
                checked={consentGiven}
                onChange={setConsentGiven}
                assisted={!publicToken}
                items={['Name, PAN, date of birth, contact & address', 'Bank & nominee details', 'Photograph, PAN, Aadhaar and bank proof documents']}
              />

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  {publicToken ? 'Submit Onboarding' : 'Complete Onboarding'}
                </Button>
                {!publicToken && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/admin/onboarding')}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateOnboarding;
